import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Users, Plus, Trash2, Save, Copy, Check, FileDown, Link as LinkIcon, PenTool, Loader2, ChevronDown, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ADDITIONAL_OWNERS = 4; // 5 total — primary owner + 4

// Robust clipboard copy that works even when navigator.clipboard is unavailable.
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through to legacy */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
};

const blankOwner = () => ({
  first_name: '', last_name: '', dob: '',
  street_address: '', apt_suite: '', city: '', state: '', zip: '',
  ssn: '', ownership_percentage: '', email: '', phone: '',
});

// Strip blanks + coerce ownership_percentage to a real number so FastAPI/Pydantic accepts it.
const toServerPayload = (owner) => {
  const p = { ...owner };
  delete p.id;
  Object.keys(p).forEach(k => { if (p[k] === '') p[k] = null; });
  if (p.ownership_percentage != null) {
    const n = parseFloat(p.ownership_percentage);
    p.ownership_percentage = Number.isFinite(n) ? n : null;
  }
  return p;
};

// FastAPI 422 returns detail as an array of error objects. Sonner forwards
// arrays/objects to React which then crashes. This helper coerces anything
// safely to a single human-readable string.
const errorMessage = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
  if (typeof d === 'object') return d.msg || JSON.stringify(d);
  return fallback;
};

const OwnerCard = ({ owner, onChange, onSave, onDelete, onCopyLink, onDownload, copiedKey, saving, applicationId, expanded, onToggleExpanded }) => {
  const [showSsn, setShowSsn] = useState(false);
  const ownerLabel = `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || 'New Owner';
  return (
    <div className="border border-slate-200 rounded-xl bg-white" data-testid={`jumbo-owner-card-${owner.id}`}>
      {/* Header row — title on top, action buttons wrap below on narrow screens */}
      <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50 rounded-t-xl">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <button
            onClick={onToggleExpanded}
            className="flex items-center gap-1.5 text-left text-sm font-semibold text-slate-800 min-w-0 flex-1"
            data-testid={`jumbo-owner-toggle-${owner.id}`}
          >
            {expanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
            <Users className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <span className="truncate">{ownerLabel}</span>
            {owner.ownership_percentage ? (
              <span className="text-xs text-slate-500 font-normal flex-shrink-0">· {owner.ownership_percentage}%</span>
            ) : null}
          </button>
          <Button
            size="sm" variant="ghost"
            className="text-xs text-red-600 hover:bg-red-50 h-7 w-7 p-0 flex-shrink-0"
            onClick={() => onDelete(owner)}
            data-testid={`jumbo-owner-delete-${owner.id}`}
            title="Remove this additional owner"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        {/* Action buttons — wrap to next line in narrow containers */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Button
            size="sm" variant="outline"
            className="text-[11px] h-7 px-2 border-teal-400 text-teal-700 hover:bg-teal-50"
            onClick={() => onDownload(owner)}
            data-testid={`jumbo-owner-download-${owner.id}`}
          >
            <FileDown className="h-3 w-3 mr-1" /> Download
          </Button>
          <Button
            size="sm" variant="outline"
            className="text-[11px] h-7 px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => onCopyLink(owner, 'info')}
            data-testid={`jumbo-owner-copy-info-${owner.id}`}
          >
            {copiedKey === `${owner.id}:info`
              ? <><Check className="h-3 w-3 mr-1" /> Copied</>
              : <><LinkIcon className="h-3 w-3 mr-1" /> Info Link</>}
          </Button>
          <Button
            size="sm" variant="outline"
            className="text-[11px] h-7 px-2 border-amber-300 text-amber-800 hover:bg-amber-50"
            onClick={() => onCopyLink(owner, 'sign')}
            data-testid={`jumbo-owner-copy-sign-${owner.id}`}
          >
            {copiedKey === `${owner.id}:sign`
              ? <><Check className="h-3 w-3 mr-1" /> Copied</>
              : <><PenTool className="h-3 w-3 mr-1" /> Sign Link</>}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Legal First Name</Label>
            <Input value={owner.first_name || ''} onChange={e => onChange({ ...owner, first_name: e.target.value })} data-testid={`jumbo-owner-input-first_name-${owner.id}`} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Legal Last Name</Label>
            <Input value={owner.last_name || ''} onChange={e => onChange({ ...owner, last_name: e.target.value })} data-testid={`jumbo-owner-input-last_name-${owner.id}`} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">DOB (MM/DD/YYYY)</Label>
            <Input value={owner.dob || ''} placeholder="MM/DD/YYYY" onChange={e => onChange({ ...owner, dob: e.target.value })} data-testid={`jumbo-owner-input-dob-${owner.id}`} className="h-9" />
          </div>

          <div className="sm:col-span-2 xl:col-span-2">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Home Street Address</Label>
            <Input value={owner.street_address || ''} onChange={e => onChange({ ...owner, street_address: e.target.value })} data-testid={`jumbo-owner-input-street_address-${owner.id}`} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Apt / Suite</Label>
            <Input value={owner.apt_suite || ''} onChange={e => onChange({ ...owner, apt_suite: e.target.value })} data-testid={`jumbo-owner-input-apt_suite-${owner.id}`} className="h-9" />
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">City</Label>
            <Input value={owner.city || ''} onChange={e => onChange({ ...owner, city: e.target.value })} data-testid={`jumbo-owner-input-city-${owner.id}`} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">State</Label>
            <Input value={owner.state || ''} onChange={e => onChange({ ...owner, state: e.target.value })} data-testid={`jumbo-owner-input-state-${owner.id}`} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">ZIP</Label>
            <Input value={owner.zip || ''} onChange={e => onChange({ ...owner, zip: e.target.value })} data-testid={`jumbo-owner-input-zip-${owner.id}`} className="h-9" />
          </div>

          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Social Security #</Label>
            <div className="relative">
              <Input type={showSsn ? 'text' : 'password'} value={owner.ssn || ''} placeholder="XXX-XX-XXXX" onChange={e => onChange({ ...owner, ssn: e.target.value })} data-testid={`jumbo-owner-input-ssn-${owner.id}`} className="h-9 pr-12" />
              <button type="button" onClick={() => setShowSsn(!showSsn)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-500 hover:text-slate-700">
                {showSsn ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Ownership %</Label>
            <Input type="number" value={owner.ownership_percentage || ''} onChange={e => onChange({ ...owner, ownership_percentage: e.target.value })} data-testid={`jumbo-owner-input-ownership_percentage-${owner.id}`} className="h-9" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Email (for invites)</Label>
            <Input type="email" value={owner.email || ''} onChange={e => onChange({ ...owner, email: e.target.value })} data-testid={`jumbo-owner-input-email-${owner.id}`} className="h-9" />
          </div>

          <div className="sm:col-span-2 xl:col-span-3 pt-2 flex justify-end">
            <Button size="sm" onClick={() => onSave(owner)} disabled={saving} className="bg-[#0E333F] hover:bg-[#072028] text-white" data-testid={`jumbo-owner-save-${owner.id}`}>
              {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save Owner</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const JumboAdditionalOwners = ({ application, authToken, onUpdated }) => {
  const initialOwners = useMemo(() => application?.jumbo_term_loan_data?.additional_owners || [], [application]);
  const [owners, setOwners] = useState(initialOwners);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => { setOwners(initialOwners); }, [initialOwners]);

  const updateLocal = (ownerId, next) => {
    setOwners(prev => prev.map(o => (o.id === ownerId ? next : o)));
  };

  const handleAdd = async () => {
    if (owners.length >= MAX_ADDITIONAL_OWNERS) {
      toast.error('Maximum of 4 additional owners reached.');
      return;
    }
    setAdding(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/applications/${application.id}/jumbo-owners`,
        toServerPayload(blankOwner()),
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setOwners([...owners, res.data]);
      setExpanded(prev => ({ ...prev, [res.data.id]: true }));
      toast.success('Additional owner added.');
      onUpdated?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not add owner.'));
    } finally {
      setAdding(false);
    }
  };

  const handleSave = async (owner) => {
    setSaving(true);
    try {
      const payload = toServerPayload(owner);
      await axios.put(
        `${BACKEND_URL}/api/applications/${application.id}/jumbo-owners/${owner.id}`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success('Owner saved.');
      onUpdated?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (owner) => {
    if (!window.confirm(`Remove additional owner ${owner.first_name || ''} ${owner.last_name || ''}?`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/applications/${application.id}/jumbo-owners/${owner.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setOwners(prev => prev.filter(o => o.id !== owner.id));
      toast.success('Owner removed.');
      onUpdated?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Delete failed.'));
    }
  };

  const handleCopyLink = async (owner, linkType) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/applications/${application.id}/jumbo-owners/${owner.id}/link`,
        { link_type: linkType },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const path = linkType === 'info' ? 'jumbo-owner-info' : 'jumbo-owner-sign';
      const url = `${window.location.origin}/${path}/${res.data.token}`;
      const copied = await copyToClipboard(url);
      if (copied) {
        setCopiedKey(`${owner.id}:${linkType}`);
        toast.success(`${linkType === 'info' ? 'Owner info' : 'Signature'} link copied to clipboard.`);
      } else {
        // Fallback: show the URL in the toast so the broker can copy manually.
        toast.success(
          `Link generated (clipboard blocked). Copy: ${url}`,
          { duration: 12000 }
        );
      }
      setTimeout(() => setCopiedKey(null), 2200);
    } catch (e) {
      toast.error(errorMessage(e, 'Could not create link.'));
    }
  };

  const handleDownload = (owner) => {
    const url = `${BACKEND_URL}/api/applications/${application.id}/pdf/jumbo-term-loan?owner_id=${owner.id}&token=${authToken}`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.click();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="jumbo-additional-owners-panel">
      {/* Panel header — clean vertical stack so nothing wraps awkwardly at narrow widths */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-5 w-5 text-[#0E333F] flex-shrink-0" />
          <h3 className="text-base font-bold text-slate-800 leading-tight">Additional Owners</h3>
          <span className="text-xs text-slate-500 font-medium">· 20%+ stake</span>
          {owners.length > 0 && (
            <span className="ml-auto text-[11px] text-slate-500 font-medium bg-slate-100 px-2 py-0.5 rounded-full" data-testid="jumbo-owners-count">
              {owners.length} of {MAX_ADDITIONAL_OWNERS}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 leading-snug mb-3">
          Up to 5 total owners with 20%+ stake (incl. primary). Send each one an info link to collect their data, then a signature link to e-sign their own copy.
        </p>
        <Button
          onClick={handleAdd}
          disabled={adding || owners.length >= MAX_ADDITIONAL_OWNERS}
          className="w-full sm:w-auto bg-[#0E333F] hover:bg-[#072028] text-white h-9 px-4 text-sm"
          data-testid="jumbo-owners-add-btn"
        >
          {adding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
          Add Owner
        </Button>
      </div>

      {owners.length === 0 ? (
        <div className="text-sm text-slate-500 italic px-3 py-6 text-center bg-slate-50 border border-dashed border-slate-200 rounded-lg">
          No additional owners yet. The primary owner is already on the main application.
        </div>
      ) : (
        <div className="space-y-3">
          {owners.map(o => (
            <OwnerCard
              key={o.id}
              owner={o}
              expanded={!!expanded[o.id]}
              onToggleExpanded={() => setExpanded(prev => ({ ...prev, [o.id]: !prev[o.id] }))}
              onChange={(next) => updateLocal(o.id, next)}
              onSave={handleSave}
              onDelete={handleDelete}
              onCopyLink={handleCopyLink}
              onDownload={handleDownload}
              copiedKey={copiedKey}
              saving={saving}
              applicationId={application.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default JumboAdditionalOwners;
